/*
 * Quem está conectado agora, por equipamento.
 *
 * A lista vem do MikroTik, não do banco: sessão que caiu por queda de energia
 * ou acesso liberado por outro caminho nunca passam pelo nosso registro.
 * Mostrar a nossa versão como se fosse a realidade enganaria quem atende.
 */
import { useCallback, useEffect, useState } from 'react';
import { PERMISSIONS } from '@hotspot/shared';
import { ApiRequestError, apiList, apiRequest } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  formatarBytes,
  formatarDuracao,
  type LiveSessionDto,
  type MikrotikDto,
  type SessionHistoryDto,
} from '../lib/types';
import { Etiqueta, dataCurta } from './CustomersPage';
import { Alert, Button, Card, Field, Spinner } from '../components/ui';

/** Atualiza sozinho: quem olha esta tela quer o estado de agora. */
const INTERVALO_MS = 15_000;

const ROTULO_METODO: Record<string, string> = {
  open: 'Aberto',
  register: 'Cadastro',
  hubsoft: 'HubSoft',
  manual: 'Manual',
};

const ROTULO_STATUS: Record<string, string> = {
  pending: 'Pendente',
  authorized: 'Autorizada',
  active: 'Ativa',
  closed: 'Encerrada',
  denied: 'Negada',
  expired: 'Expirada',
};

export function SessionsPage() {
  const { can } = useAuth();
  const podeAgir = can(PERMISSIONS.SESSION_ACTION);

  const [equipamentos, setEquipamentos] = useState<MikrotikDto[] | null>(null);
  const [selecionado, setSelecionado] = useState<number | null>(null);

  useEffect(() => {
    apiList<MikrotikDto>('/admin/mikrotiks?perPage=100')
      .then(({ data }) => {
        setEquipamentos(data);
        if (data.length > 0) setSelecionado(data[0]!.id);
      })
      .catch(() => setEquipamentos([]));
  }, []);

  if (!equipamentos) return <Spinner label="Carregando equipamentos..." />;

  if (equipamentos.length === 0) {
    return (
      <Alert tone="info">
        Nenhum MikroTik cadastrado. Cadastre um equipamento para acompanhar as sessões.
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-slate-900">Sessões</h1>
        <p className="text-sm text-slate-500">
          Quem está conectado agora, lido diretamente do equipamento.
        </p>
      </header>

      {equipamentos.length > 1 ? (
        <Card>
          <div className="flex flex-wrap gap-2">
            {equipamentos.map((equipamento) => (
              <Button
                key={equipamento.id}
                variant={selecionado === equipamento.id ? 'primary' : 'ghost'}
                onClick={() => setSelecionado(equipamento.id)}
              >
                {equipamento.name}
              </Button>
            ))}
          </div>
        </Card>
      ) : null}

      {selecionado !== null ? (
        <PainelDoEquipamento mikrotikId={selecionado} podeAgir={podeAgir} />
      ) : null}
    </div>
  );
}

function PainelDoEquipamento({
  mikrotikId,
  podeAgir,
}: {
  mikrotikId: number;
  podeAgir: boolean;
}) {
  const [sessoes, setSessoes] = useState<LiveSessionDto[] | null>(null);
  const [historico, setHistorico] = useState<SessionHistoryDto[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [atualizadoEm, setAtualizadoEm] = useState<Date | null>(null);

  const carregar = useCallback(
    async (mostrarCarregando = false) => {
      if (mostrarCarregando) setSessoes(null);
      try {
        const [ativas, passadas] = await Promise.all([
          apiRequest<LiveSessionDto[]>(`/admin/mikrotiks/${mikrotikId}/sessions`),
          apiRequest<SessionHistoryDto[]>(`/admin/mikrotiks/${mikrotikId}/sessions/history`),
        ]);
        setSessoes(ativas);
        setHistorico(passadas);
        setErro(null);
        setAtualizadoEm(new Date());
      } catch (caught) {
        /*
         * Lista vazia seria lida como "ninguem conectado", que e o oposto de
         * "nao consegui falar com o equipamento". Mantemos a ultima leitura e
         * dizemos que ela envelheceu.
         */
        setErro(
          caught instanceof ApiRequestError
            ? caught.displayMessage
            : 'Não foi possível falar com o equipamento.',
        );
      }
    },
    [mikrotikId],
  );

  useEffect(() => {
    void carregar(true);
    const timer = window.setInterval(() => void carregar(), INTERVALO_MS);
    return () => window.clearInterval(timer);
  }, [carregar]);

  async function agir(
    chave: string,
    caminho: string,
    corpo: unknown,
    mensagem: (resposta: never) => string,
  ) {
    setOcupado(chave);
    setErro(null);
    setAviso(null);
    try {
      const resposta = await apiRequest<never>(caminho, { method: 'POST', body: corpo });
      setAviso(mensagem(resposta));
      await carregar();
    } catch (caught) {
      setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha na operação');
    } finally {
      setOcupado(null);
    }
  }

  return (
    <div className="space-y-6">
      {erro ? <Alert>{erro}</Alert> : null}
      {aviso ? <Alert tone="success">{aviso}</Alert> : null}

      <LiberacaoManual
        mikrotikId={mikrotikId}
        podeAgir={podeAgir}
        onFeito={(texto) => {
          setAviso(texto);
          void carregar();
        }}
        onErro={setErro}
      />

      <Card
        title="Conectados agora"
        action={
          <div className="flex items-center gap-3">
            {atualizadoEm ? (
              <span className="text-xs text-slate-500">
                atualizado {atualizadoEm.toLocaleTimeString('pt-BR')}
              </span>
            ) : null}
            <Button variant="ghost" onClick={() => void carregar(true)}>
              Atualizar
            </Button>
          </div>
        }
      >
        {!sessoes ? (
          <Spinner label="Consultando o equipamento..." />
        ) : sessoes.length === 0 ? (
          <p className="text-sm text-slate-500">Ninguém conectado neste equipamento agora.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-xs tracking-wide text-slate-500 uppercase">
                  <th className="py-2 pr-3">Quem</th>
                  <th className="py-2 pr-3">MAC</th>
                  <th className="py-2 pr-3">IP</th>
                  <th className="py-2 pr-3">VLAN</th>
                  <th className="py-2 pr-3">Tempo</th>
                  <th className="py-2 pr-3">Tráfego</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {sessoes.map((sessao) => (
                  <tr key={sessao.routerId} className="border-b border-slate-100 last:border-0">
                    <td className="py-2.5 pr-3">
                      {sessao.customerName ? (
                        <span className="font-medium text-slate-800">{sessao.customerName}</span>
                      ) : sessao.conhecido ? (
                        <span className="text-slate-500">Sem cadastro</span>
                      ) : (
                        <Etiqueta tom="slate">Fora do portal</Etiqueta>
                      )}
                      {sessao.deviceBlocked ? (
                        <span className="ml-2">
                          <Etiqueta tom="red">bloqueado</Etiqueta>
                        </span>
                      ) : null}
                    </td>
                    <td className="py-2.5 pr-3 font-mono text-xs text-slate-600">
                      {sessao.mac ?? '—'}
                      {sessao.isRandomizedMac ? (
                        <span
                          className="ml-1 cursor-help text-slate-400"
                          title="Endereço aleatório: o aparelho troca de MAC a cada rede."
                        >
                          *
                        </span>
                      ) : null}
                    </td>
                    <td className="py-2.5 pr-3 font-mono text-xs text-slate-600">
                      {sessao.ip ?? '—'}
                    </td>
                    <td className="py-2.5 pr-3 font-mono text-xs text-slate-600">
                      {sessao.vlan ?? <span className="font-sans text-slate-400">—</span>}
                    </td>
                    <td className="py-2.5 pr-3 text-slate-700">
                      {formatarDuracao(sessao.uptimeSeconds)}
                    </td>
                    <td className="py-2.5 pr-3 text-xs text-slate-600">
                      {/* No RouterOS, bytes-out e o que o roteador entrega ao cliente. */}
                      ↓ {formatarBytes(sessao.bytesOut)} · ↑ {formatarBytes(sessao.bytesIn)}
                    </td>
                    <td className="py-2.5 text-right whitespace-nowrap">
                      {podeAgir && sessao.mac ? (
                        <>
                          <Button
                            variant="ghost"
                            disabled={ocupado !== null}
                            onClick={() =>
                              void agir(
                                `d-${sessao.routerId}`,
                                `/admin/mikrotiks/${mikrotikId}/sessions/disconnect`,
                                { mac: sessao.mac },
                                () => `${sessao.mac} desconectado. Terá que autenticar de novo.`,
                              )
                            }
                          >
                            {ocupado === `d-${sessao.routerId}` ? '...' : 'Desconectar'}
                          </Button>
                          <Button
                            variant="ghost"
                            disabled={ocupado !== null}
                            onClick={() =>
                              void agir(
                                `b-${sessao.routerId}`,
                                `/admin/mikrotiks/${mikrotikId}/devices/block`,
                                { mac: sessao.mac, blocked: true },
                                () => `${sessao.mac} bloqueado e desconectado.`,
                              )
                            }
                          >
                            Bloquear
                          </Button>
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <p className="mt-3 text-xs text-slate-500">
              <strong>Desconectar</strong> apaga o cookie de login do roteador, então o cliente
              volta para o portal — não reconecta sozinho. <strong>Bloquear</strong> é por
              endereço MAC: celulares trocam de MAC por rede, então serve contra abuso
              oportunista, não para barrar alguém determinado.
            </p>
          </div>
        )}
      </Card>

      <Card title="Últimos acessos">
        {historico.length === 0 ? (
          <p className="text-sm text-slate-500">Nenhum acesso registrado neste equipamento.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-xs tracking-wide text-slate-500 uppercase">
                  <th className="py-2 pr-3">Quando</th>
                  <th className="py-2 pr-3">Quem</th>
                  <th className="py-2 pr-3">MAC</th>
                  <th className="py-2 pr-3">Modo</th>
                  <th className="py-2">Situação</th>
                </tr>
              </thead>
              <tbody>
                {historico.map((sessao) => (
                  <tr key={sessao.id} className="border-b border-slate-100 last:border-0">
                    <td className="py-2 pr-3 text-slate-700">{dataCurta(sessao.startedAt)}</td>
                    <td className="py-2 pr-3 text-slate-700">{sessao.customerName ?? '—'}</td>
                    <td className="py-2 pr-3 font-mono text-xs text-slate-600">{sessao.mac}</td>
                    <td className="py-2 pr-3 text-slate-600">
                      {ROTULO_METODO[sessao.authMethod] ?? sessao.authMethod}
                    </td>
                    <td className="py-2 text-slate-600">
                      {ROTULO_STATUS[sessao.status] ?? sessao.status}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

/** Liberação de balcão: cliente com o navegador travado, aparelho sem portal. */
function LiberacaoManual({
  mikrotikId,
  podeAgir,
  onFeito,
  onErro,
}: {
  mikrotikId: number;
  podeAgir: boolean;
  onFeito: (mensagem: string) => void;
  onErro: (mensagem: string) => void;
}) {
  const [aberto, setAberto] = useState(false);
  const [mac, setMac] = useState('');
  const [minutos, setMinutos] = useState('60');
  const [ocupado, setOcupado] = useState(false);

  if (!podeAgir) return null;

  if (!aberto) {
    return (
      <div>
        <Button variant="ghost" onClick={() => setAberto(true)}>
          + Liberar um aparelho manualmente
        </Button>
      </div>
    );
  }

  async function liberar() {
    setOcupado(true);
    try {
      await apiRequest(`/admin/mikrotiks/${mikrotikId}/sessions/authorize`, {
        method: 'POST',
        body: { mac: mac.trim(), minutes: Number(minutos) || 60 },
      });
      onFeito(`${mac.trim()} liberado por ${minutos} minutos.`);
      setMac('');
      setAberto(false);
    } catch (caught) {
      onErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao liberar');
    } finally {
      setOcupado(false);
    }
  }

  return (
    <Card
      title="Liberar aparelho manualmente"
      action={
        <Button variant="ghost" onClick={() => setAberto(false)}>
          Cancelar
        </Button>
      }
    >
      <div className="space-y-3">
        <Alert tone="warning">
          O acesso é liberado <strong>sem passar pelo portal</strong>: não há aceite de termos nem
          cadastro. Fica registrado como acesso manual, com o seu usuário na auditoria.
        </Alert>

        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[220px] flex-1">
            <Field
              label="Endereço MAC do aparelho"
              value={mac}
              onChange={(evento) => setMac(evento.target.value)}
              placeholder="AA:BB:CC:11:22:33"
              hint="O aparelho já precisa estar conectado ao Wi-Fi da rede de visitantes."
            />
          </div>
          <div className="w-32">
            <Field
              label="Minutos"
              type="number"
              min={5}
              max={1440}
              value={minutos}
              onChange={(evento) => setMinutos(evento.target.value)}
            />
          </div>
          <Button disabled={ocupado || mac.trim().length < 12} onClick={() => void liberar()}>
            {ocupado ? 'Liberando...' : 'Liberar'}
          </Button>
        </div>
      </div>
    </Card>
  );
}
